import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField
} from "@material-ui/core";
import { Close } from "../../../icons";
import { Button, IconButton } from '@coolremote/shared';
import { t } from "ttag";
import useStyles from "../AlertInfo.style";
import useTypes from "@hooks/useTypes";

export interface ISnoozeDuration {
  value: string;
  label: string;
}

/**
 * Props for the SnoozeDialog component.
 * @interface SnoozeDialogProps
 * @property {boolean} open - Whether the dialog is open.
 * @property {() => void} onClose - Callback function to close the dialog.
 * @property {string} snoozeError - Error message to display if snooze fails.
 * @property {(duration: number, scope: string, note: string) => void} handleSnooze - Callback function to initiate the snooze action with user-selected values.
 * @property {any} alert - The alert object being snoozed.
 * @property {boolean} [isLoading=false] - Whether the snooze operation is currently in progress.
 */
interface SnoozeDialogProps {
  open: boolean;
  onClose: () => void;
  snoozeError: string;
  handleSnooze: (duration: string, scope: string, note: string) => void;
  alert: any;
  isLoading?: boolean;
}

/**
 * A dialog component that allows users to configure and initiate an alert snooze.
 * Users can select the snooze duration and scope (unit or entire system).
 * 
 * @component
 */
const SnoozeDialog: React.FC<SnoozeDialogProps> = ({
  open,
  onClose,
  snoozeError,
  handleSnooze,
  alert,
  isLoading = false,
}) => {
  const classes: any = useStyles();
  const [duration, setDuration] = useState<string>("1h");
  const [scope, setScope] = useState<string>("unit");
  const [note, setNote] = useState<string>("");

  const { snoozeDurations } = useTypes();

  useEffect(() => {
    if (open) {
      setDuration("1h");
      setScope("unit");
      setNote("");
    }
  }, [open]);

  const onSnoozeClick = () => {
    handleSnooze(duration, scope, note);
  };

  const renderRadioGroup = () => {

    if (!alert?.systemId) {
      return null;
    }

    return (
      <RadioGroup
        value={scope}
        onChange={(e) => setScope(e.target.value as string)}
        className={classes.snoozeDialogCheckboxGroup}
      >
        <FormControlLabel
          value="unit"
          control={<Radio color="primary" />}
          label={
            <span className={classes.snoozeDialogCheckboxLabel}>
              {t`Snooze for only unit`}{" - "}
              <span style={{ fontWeight: 700 }}>{alert?.unitName || "-"}</span>
            </span>
          }
        />
        <FormControlLabel
          value="system"
          control={<Radio color="primary" />}
          label={
            <span className={classes.snoozeDialogCheckboxLabel}>
              {t`Snooze for all system units`}{" - "}
              <span style={{ fontWeight: 700 }}>{alert?.systemName || "-"}</span>
            </span>
          }
          disabled={!alert?.systemId}
        />
      </RadioGroup>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className={classes.dialogHeader}>
        <Typography className={classes.headerTitle}>
          {t`Snooze Alerts for`} {alert?.description} ({alert?.data})
        </Typography>
        <IconButton disableRipple onClick={onClose} className={classes.iconBtnStyle}>
          <Close color="#7f7692" />
        </IconButton>
      </div>
      <DialogContent className={classes.snoozeDialogContent}>

        <div className={classes.snoozeDialogSubtitleRow}>
          <Typography className={classes.snoozeDialogSubtitle}>
            {t`Snooze alerts for:`}
          </Typography>
          <FormControl variant="outlined" className={classes.snoozeDialogDurationSelect}>
            <Select
              value={duration}
              onChange={(e) => setDuration(e.target.value as string)}
            >
              {snoozeDurations.map((snoozeDuration: ISnoozeDuration) => (
                <MenuItem key={snoozeDuration.value} value={snoozeDuration.value}>
                  {snoozeDuration.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {renderRadioGroup()}

        <TextField
          variant="outlined"
          multiline
          rows={4}
          placeholder={t`Add notes for the snooze (optional)`}
          fullWidth
          className={classes.snoozeDialogNotes}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{
            maxLength: 100
          }}
        />
        {snoozeError && <Typography color="error">{snoozeError}</Typography>}
      </DialogContent>
      <div className={classes.ackActionsHolder}>
        <Button
          white
          width={150}
          marginRight
          onClick={onClose}
          disabled={isLoading}
        >
          {t`Cancel`}
        </Button>
        <Button
          onClick={onSnoozeClick}
          width={150}
          disabled={isLoading}
        >
          {isLoading ? t`Snoozing...` : t`Ok`}
        </Button>
      </div>
    </Dialog>
  );
};

export default SnoozeDialog;
